import TrashIcon from '@Resources/assets/icons/dark/trash.svg';
import { isValidEmail } from '@Utils/helpers/stringHelpers';
import { ITableEditOption, ITableEntity } from '@Utils/helpers/tableHelpers';
import { CircularProgress, SvgIcon } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import _isNil from 'lodash/isNil';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Button from '@Components/Button/Button';
import CircularLoading from '@Components/CircularLoading/CircularLoading';
import DeleteDialog from '@Components/DeleteDialog/DeleteDialog';
import IconButton from '@Components/IconButton/IconButton';
import SettingsTable, {
    SettingsTableViewState,
} from '@Components/Settings/components/SettingsTable/SettingsTable';
import SettingsTableButton from '@Components/Settings/components/SettingsTable/SettingsTableButton';
import Styles from '@Components/Settings/views/SettingsViews.scss';
import AdminApi from '@Data/administration/Api';
import { settingsSuperAdminsState } from '@Data/administration/Reducer';
import { IUpdateUser, IUser } from '@Data/users/Interfaces';
import { ISettingsBaseComponentProps } from '../../Interfaces';

const emptyUpdateUser = {
    name: '',
    email: '',
    nickname: '',
};

const SuperAdminSettingsTable = (props: ISettingsBaseComponentProps) => {
    const { title, isSubmitting, setIsSubmitting, id } = props;
    const [selectedUser, setSelectedUser] = useState<IUser | null>(null);
    const [tableState, setTableState] = useState<SettingsTableViewState>(
        SettingsTableViewState.VIEW
    );
    const superAdmins = useSelector(settingsSuperAdminsState);
    const [updateUserEntity, setUpdateUserEntity] =
        useState<IUpdateUser>(emptyUpdateUser);
    const [deleteDialogIsOpen, setDeleteDialogIsOpen] =
        useState<boolean>(false);
    const columnHelper = createColumnHelper<IUser>();
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
        getSuperAdmins();
    }, []);

    const getSuperAdmins = async () => {
        setIsLoading(true);
        await AdminApi.getAllSuperAdmins(() => setIsLoading(false));
    };

    const columns = [
        columnHelper.accessor('email', {
            cell: (info) => info.getValue(),
            header: 'Super Admin IKE ID',
        }),
        columnHelper.accessor('nickname', {
            cell: (info) => {
                const og = info.row.original;
                return og.given_name && og.family_name
                    ? `${og.given_name} ${og.family_name}`
                    : '-';
            },
            header: 'Name',
        }),
        columnHelper.accessor('actions', {
            cell: null,
            header: 'Actions',
            enableSorting: false,
        }),
    ];

    const submitCleanup = async () => {
        setSelectedUser(null);
        setIsSubmitting(false);
        handleCancelUpdateUser();
    };

    const handleUpdateUser = async () => {
        setIsSubmitting(true);

        if (tableState === SettingsTableViewState.CREATE) {
            await AdminApi.createSuperAdmin(updateUserEntity.email);
        } else if (tableState === SettingsTableViewState.EDIT) {
            await AdminApi.deleteSuperAdmin(updateUserEntity.email);
        }

        submitCleanup();
        getSuperAdmins();
    };

    const handleSetEditState = () => {
        setUpdateUserEntity({
            email: selectedUser.email,
            nickname: selectedUser.nickname,
            name: selectedUser.name,
        });
        setTableState(SettingsTableViewState.EDIT);
    };

    const editCells: Array<ITableEditOption> = [
        { attribute: 'email', component: <input type="text" disabled /> },
        { attribute: 'nickname', component: <input type="text" disabled /> },
        {
            attribute: 'actions',
            isStandalone: true,
            component: (
                <IconButton
                    onClick={() => {
                        setUpdateUserEntity({
                            name: selectedUser.name,
                            email: selectedUser.email,
                            nickname: selectedUser.nickname,
                        });
                        setDeleteDialogIsOpen(true);
                    }}
                    style={{ backgroundColor: 'transparent' }}
                    icon={
                        <SvgIcon inheritViewBox>
                            <TrashIcon />
                        </SvgIcon>
                    }
                />
            ),
        },
    ];

    const createCells: Array<ITableEditOption> = [
        { attribute: 'email', component: <input type="text" /> },
        { attribute: 'nickname', component: null },
        { attribute: 'actions', component: null },
    ];

    const handleCancelUpdateUser = () => {
        setTableState(SettingsTableViewState.VIEW);
        setUpdateUserEntity(emptyUpdateUser);
    };

    const canCreateNewEntity = useMemo(() => {
        return (
            tableState === SettingsTableViewState.CREATE &&
            isValidEmail(updateUserEntity.email)
        );
    }, [updateUserEntity.email]);

    return (
        <div className={Styles.settingsViewWrapper}>
            <div className={Styles.settingsViewHeader}>
                <span id={`${id}-title`}>{title}</span>

                <div>
                    {tableState !== SettingsTableViewState.VIEW && (
                        <Button
                            id={`cancel-edit-${id}-btn`}
                            color={'black'}
                            onClick={handleCancelUpdateUser}
                        >
                            Cancel
                        </Button>
                    )}
                    <SettingsTableButton
                        id={`edit-${id}-table-btn`}
                        tableState={tableState}
                        canEdit={!_isNil(selectedUser)}
                        createText={'Add New Super Admin'}
                        editText={'Edit Super Admin'}
                        setCreateState={() =>
                            setTableState(SettingsTableViewState.CREATE)
                        }
                        setEditState={handleSetEditState}
                        onEditEntity={() => setDeleteDialogIsOpen(true)}
                        onCreateNewEntity={handleUpdateUser}
                        canCreateNewEntity={canCreateNewEntity}
                        isSubmitting={isSubmitting}
                        hideEditButton
                    />
                </div>
            </div>
            <div className={Styles.tableContainer}>
                {isLoading ? (
                    <CircularLoading />
                ) : (
                    <SettingsTable
                        id={`${id}-table`}
                        columns={columns}
                        data={superAdmins}
                        onRowClick={(user) => setSelectedUser(user)}
                        selectedRow={selectedUser}
                        state={tableState}
                        updateEntity={updateUserEntity}
                        onChangeUpdateEntity={setUpdateUserEntity}
                        editCells={editCells}
                        createCells={createCells}
                        isSubmitting={isSubmitting}
                    />
                )}
                <DeleteDialog
                    id={`${id}-delete-dialog`}
                    isOpen={deleteDialogIsOpen}
                    onClose={() => setDeleteDialogIsOpen(false)}
                    onDelete={() => {
                        setDeleteDialogIsOpen(false);
                        handleUpdateUser();
                    }}
                    entityName={selectedUser?.email}
                    isDeleting={isSubmitting}
                />
            </div>
        </div>
    );
};

export default SuperAdminSettingsTable;
